import { Component, inject, OnInit } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
} from '@angular/material/dialog';
import { IMessage, IRequestDetail } from '../../../../interfaces/i-chat-session';
import { MatButtonModule } from '@angular/material/button';
import { MarkdownService } from '../../services/mark-down.service';

import { MermaidDirective } from '../../directives/mermaid.directive';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-request-detail',
  standalone: true,
  imports: [
    MermaidDirective,
    MatDialogActions,
    MatDialogClose,
    MatButtonModule,
    MatDialogContent,
    MatIconModule
],
  templateUrl: './request-detail.component.html',
  styleUrl: './request-detail.component.scss'
})
export class RequestDetailComponent implements OnInit {
  readonly dialogRef = inject(MatDialogRef<RequestDetailComponent>);
  readonly data = inject<IMessage>(MAT_DIALOG_DATA);
  diagram: string = '';
  formatted: string = '';
  showError = false;

  // Create more dynamic detection of AI models
  // TODO - make this more dynamic.  This is a quick fix for now.
  knownAiPrefixes = ['GPT', 'DALL-E', 'Claude', 'Gemini', 'Copilot', 'O-'];

  #markdownService = inject(MarkdownService);

  ngOnInit() {
    this.#initializeComponent();
  }

  async #initializeComponent(): Promise<void> {
    this.showError = false;

    if (!this.data.requestDetail) {
      return;
    }

    try {
      let diagramText = `sequenceDiagram\n    autonumber\n    actor User\n    participant AI\n`;
      let participants = [...new Set(this.data.requestDetail.map(o => o.participant))];

      const models = participants.filter(participant =>
        this.knownAiPrefixes.some(prefix => participant.toLowerCase().startsWith(prefix.toLowerCase())) ||
        participant.toLowerCase().includes('model') ||
        participant.toLowerCase().includes('ai') ||
        participant.toLowerCase().includes('version')
      );
      participants = participants.filter(o => !models.includes(o));
      participants.forEach((v, i) => diagramText += `    participant A${i} as ${v.replace(/-/gi, ' ')}\n`);
      const markdownText = [];
      let prevEntry: IRequestDetail = { participant: 'User', forward: true, detailMarkdown: '' };
      let leftPart: string;
      let rightPart: string;
      let lastModelRef: number | undefined;
      let modelRef: number | undefined;
      let detailText: string;
      let firstEntry = true;
      this.data.requestDetail.forEach((o, i) => {
        leftPart = prevEntry.participant;
        rightPart = o.participant;
        if (leftPart !== 'User') {
          const isModel = models.some(model => leftPart === model);
          if (isModel) {
            modelRef = models.indexOf(leftPart) + 1;
            leftPart = 'AI';
          } else {
            leftPart = `A${participants.indexOf(leftPart)}`;
          }
        }
        if (rightPart !== 'User') {
          const isModel = models.some(model => rightPart === model);
          if (isModel) {
            modelRef = models.indexOf(rightPart) + 1;
            rightPart = 'AI';
          } else {
            rightPart = `A${participants.indexOf(rightPart)}`;
          }
        }
        const arrowText = prevEntry.forward === true ? '->>' : '-->>';
        diagramText += `    ${leftPart}${arrowText}${rightPart}: ...\n`;
        if (firstEntry === true) {
          firstEntry = false;
          detailText = `1. User made the request to the AI.`;
        } else {
          detailText = `${i + 2}. ${prevEntry.detailMarkdown}`;
        }
        if (modelRef !== lastModelRef) {
          detailText = detailText.replace(/AI/g, `AI[^${modelRef}]`);
        }
        lastModelRef = modelRef;
        markdownText.push(detailText);
        prevEntry = o;
      });
      leftPart = prevEntry.participant;
      if (leftPart !== 'User') {
        if (leftPart.startsWith('GPT') || leftPart.startsWith('DALL-E')) {
          modelRef = models.indexOf(leftPart) + 1;
          leftPart = 'AI';
        } else {
          leftPart = `A${participants.indexOf(leftPart)}`;
        }
      }
      rightPart = 'User';
      const arrowText = prevEntry.forward === true ? '->>' : '-->>';
      diagramText += `    ${leftPart}${arrowText}${rightPart}: ...\n`;
      detailText = `${this.data.requestDetail.length + 1}. ${prevEntry.detailMarkdown}`;
      if (modelRef !== lastModelRef) {
        detailText = detailText.replace(/AI/g, `AI[^${modelRef}]`);
      }
      markdownText.push(detailText);
      models.forEach((v, i) => {
        markdownText.push(`[^${i + 1}]: ${v}`);
      });
      this.diagram = diagramText;
      const processedText = await this.#markdownService.processMarkdown(markdownText.join('\n'));
      let newText = processedText.replace(`<h2 id="user-content-footnote-label" class="sr-only">Footnotes</h2>`, `<h3 id="user-content-footnote-label" class="sr-only">Footnotes</h3>`);
      newText = newText.replace(`<a class=\"data-footnote-backref\" aria-label=\"Back to reference 1\" data-footnote-backref=\"\" href=\"#user-content-fnref-1\">↩</a>`, ``);
      newText = newText.replace(`<sup><a aria-describedby=\"user-content-footnote-label\" data-footnote-ref=\"\" id=\"user-content-user-content-fnref-1\" href=\"#user-content-fn-1\">1</a></sup>`,``);
      this.formatted = newText;
    } catch (err) {
      this.showError = true;
    }
  }

  onNoClick(): void {
    this.dialogRef.close();
  }
}
